import { produce } from 'immer'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate, useSearchParams } from 'react-router-dom'

import { Button, FormGroup, InputGroup, Intent, ButtonGroup, Divider } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import { useRootOutletContext } from '../root/Root'
import styles from './ResetPassword.module.scss'

export default (props: {
    redirectWhenAuthenticated: To,
    navigateWhenLetUserSignIn: (username: string) => To,
    signInLink: (currentSearchParams: URLSearchParams) => To,
}) => {
    const auth = useAuth()
    const [searchParams,] = useSearchParams()
    const { toaster } = useRootOutletContext()

    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    const [state, setState] = useState<{
        username: string
        verificationCode: string
        newPassword: string
        confirmPassword: string
        step: "resend" | "reset"
        obscurityPassword: boolean
    }>({
        username: searchParams.get("username") || "",
        verificationCode: "",
        newPassword: "",
        confirmPassword: "",
        step: "resend",
        obscurityPassword: true,
    })

    // if auth state is changed, let user navigate to next page
    useEffect(() => {
        if (auth.isAuthenticated) navigate(props.redirectWhenAuthenticated)
    }, [auth, navigate, props])

    const executeResend = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const result = await auth.forgotPassword(state.username, { lang: i18n.language })

        if (!result.success)
            return toaster?.show({ message: t('toast.invalidUsername.message'), icon: IconNames.Error, intent: Intent.DANGER })

        setState(produce(draft => {
            draft.step = "reset"
        }))
        toaster?.show({ message: t('toast.verificationCodeSent.message'), icon: IconNames.SendMessage, intent: Intent.SUCCESS })
    }

    const executeReset = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const result = await auth.resetPassword(state.username, state.verificationCode, state.newPassword)

        if (!result.success)
            return toaster?.show({ message: t('toast.invalidVerificationCode.message'), icon: IconNames.Error, intent: Intent.DANGER })

        toaster?.show({ message: t('toast.passwordReset.message'), icon: IconNames.Endorsed, intent: Intent.SUCCESS })
        navigate(props.navigateWhenLetUserSignIn(state.username))
    }

    const toggleObscurityPasswordButton = <Button
        icon={state.obscurityPassword ? IconNames.EyeOpen : IconNames.EyeOff}
        onClick={() => setState(produce(draft => {
            draft.obscurityPassword = !draft.obscurityPassword
        }))}
        minimal
    />

    return <>
        <h1
            className={styles.title}
        >{t('forgotPassword.title')}</h1>
        {state.step === "resend" && <>
            <form onSubmit={executeResend}>
                <FormGroup
                    label={t('forgotPassword.form.usernameInput.label')}
                    labelFor="username"
                    labelInfo={t('common.requiredInput.labelInfo')}>
                    <InputGroup
                        id="username"
                        leftIcon={IconNames.Envelope}
                        type="email"
                        autoComplete="email"
                        value={state.username}
                        onChange={e => setState(produce(draft => {
                            draft.username = e.target.value
                        }))}
                        required
                    />
                </FormGroup>
                <Button
                    className={styles.submitButton}
                    type="submit"
                    icon={IconNames.SendMessage}
                    intent={Intent.PRIMARY}
                    text={t('forgotPassword.form.requestVerificationCodeButton.label')} />
            </form>
        </>}

        {state.step === "reset" && <>
            <form onSubmit={executeReset}>
                <FormGroup
                    label={t('forgotPassword.form.verificationCodeInput.label')}
                    labelFor="verificationCode"
                    labelInfo={t('common.requiredInput.labelInfo')}
                    subLabel={t('forgotPassword.form.verificationCodeInput.subLabel')}>
                    <InputGroup
                        id="verificationCode"
                        type="number"
                        autoComplete="off"
                        value={state.verificationCode}
                        onChange={e => setState(produce(draft => {
                            draft.verificationCode = e.target.value
                        }))}
                        required
                    />
                </FormGroup>
                <br />
                <FormGroup
                    label={t('forgotPassword.form.newPasswordInput.label')}
                    labelFor="newPassword"
                    labelInfo={t('common.requiredInput.labelInfo')}
                    subLabel={t('forgotPassword.form.newPasswordInput.subLabel')}>
                    <InputGroup
                        id="newPassword"
                        leftIcon={IconNames.Lock}
                        rightElement={toggleObscurityPasswordButton}
                        type={state.obscurityPassword ? "password" : "text"}
                        autoComplete="new-password"
                        value={state.newPassword}
                        onChange={e => setState(produce(draft => {
                            draft.newPassword = e.target.value
                        }))}
                        required
                        pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,}"
                    />
                </FormGroup>
                <FormGroup
                    label={t('forgotPassword.form.confirmPasswordInput.label')}
                    labelFor="confirmPassword"
                    labelInfo={t('common.requiredInput.labelInfo')}>
                    <InputGroup
                        id="confirmPassword"
                        leftIcon={IconNames.Lock}
                        rightElement={toggleObscurityPasswordButton}
                        type={state.obscurityPassword ? "password" : "text"}
                        autoComplete="new-password"
                        value={state.confirmPassword}
                        onChange={e => setState(produce(draft => {
                            draft.confirmPassword = e.target.value
                        }))}
                        required
                        pattern={`^${state.newPassword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`}
                    />
                </FormGroup>
                <Button
                    className={styles.submitButton}
                    type="submit"
                    icon={IconNames.Reset}
                    intent={Intent.SUCCESS}
                    text={t('forgotPassword.form.resetPasswordButton.label')} />
            </form>
        </>}

        <Divider />
        <ButtonGroup
            className={styles.footerButtonGroup}
            vertical
            minimal>
            <Button
                text={t('signUp.navigateToSignInButton.label')}
                rightIcon="arrow-right"
                onClick={() => navigate(props.signInLink(searchParams))}
            />
        </ButtonGroup>
    </>
}
